import { CommonModule } from '@angular/common';
import { Component, inject, Injector, Input, ViewChild } from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable, first } from 'rxjs';
import {
  DocLineDeletedDto,
  DocLineMovedDto,
  DocLineRole,
  DocLineUpdatedDto,
  SupplierReturnNoteDto,
  SupplierReturnNoteService,
  SupplierReturnStatus,
} from '@digiwall/goliath-documents/proxy';
import {
  BaseThirdPartyDocLineEditor,
  IStockDocLineEditor,
  IStockDocService,
  LinesEditorComponent,
  StockDocLineEditorHelperService,
} from '../../../../components';
import { StorageLocationService, VatType } from '@digiwall/goliath-core/proxy';
import { GoliathCoreEnumerationTypeStore } from '@digiwall/goliath-core';
import { FieldTypes } from '@digiwall/ag-grid';
import { ObserverQueue } from '@digiwall/core';
import { DocType } from '../../../base-docs/detail/doc-type';
import { ArticleProductDimension, ArticleProductRole } from '@digiwall/goliath-products/proxy';

@Component({
  selector: 'document-supplier-return-note-lines-grid',
  templateUrl: './supplier-return-note-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [StockDocLineEditorHelperService, ObserverQueue],
})
export class SupplierReturnNoteLinesEditorComponent
  extends BaseThirdPartyDocLineEditor<SupplierReturnNoteDto>
  implements IStockDocLineEditor<DocLineUpdatedDto, DocLineMovedDto, DocLineDeletedDto>
{
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);

  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  public get document() {
    return this._document;
  }

  public get Readonly() {
    return this._readonly;
  }

  get stockDocService(): IStockDocService {
    return this._docService as any;
  }
  get updateLines(): any {
    return this._updateLines;
  }

  @Input() set supplierReturnNote(value: SupplierReturnNoteDto) {
    this._document = value;
    this.setLanguageCode();
  }

  @Input() set readonly(value: boolean) {
    this._readonly = value;
  }

  protected override handlingLinesUpdatedEvent = true;

  protected articleLineRole: DocLineRole = DocLineRole.SupplierReturnNoteArticle;
  protected textLineRole: DocLineRole = DocLineRole.DocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;
  protected SupplierReturnStatus = SupplierReturnStatus;

  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticlePurchasableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];

  constructor(
    localizationService: LocalizationService,
    private service: SupplierReturnNoteService,
    protected storageLocationService: StorageLocationService,
    injector: Injector,
    private editorHelper: StockDocLineEditorHelperService<
      DocLineUpdatedDto,
      DocLineMovedDto,
      DocLineDeletedDto
    >,
    public updateQueue: ObserverQueue,
  ) {
    super(localizationService, service, injector, updateQueue);
    editorHelper.DocLineEditor = this;
    this.initColDefs();
  }

  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.push(
      ...([
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Purchase),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Sales),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
        this.editorHelper.colDef_StockLocation,
        this.colDef_TotalReceivedQuantity,
        this.colDef_RemainingQuantityToReceive,
        ...(this.stockFeature.isEnabled
          ? [
              this.getColDef_TotalReceivedQuantityStock(),
              this.getColDef_RemainingQuantityToReceiveStock(),
            ]
          : []),
        {
          field: 'supplierDiscount',
          width: 120,
          headerName: this.localizationService.instant('Doc::SupplierReturnNote:Discount'),
          type: FieldTypes.String,
          suppressNavigable: true,

          editable: params => this.isCellEditable(params),
        },
        {
          field: 'additionalPercentage',
          width: 120,
          headerName: this.localizationService.instant(
            'Doc::SupplierReturnNote:AdditionalPercentage',
          ),
          type: FieldTypes.String,
          suppressNavigable: true,

          editable: params => this.isCellEditable(params),
        },
        {
          field: 'additionalCostPrice',
          width: 120,
          headerName: this.localizationService.instant('Doc::SupplierReturnNote:AdditionalCost'),
          type: FieldTypes.String,
          suppressNavigable: true,

          editable: params => this.isCellEditable(params),
        },
      ] as Array<ColDef>),
    );
    this.markDimensionFieldsAsNonEditable(this.columnDefs);
  }

  isChapter = line => line.lineRole == DocLineRole.DocChapter;

  public override isCellEditable(params: EditableCallbackParams<any, any>) {
    let isEditable = true;

    if (this.document.returnStatus == SupplierReturnStatus.Closed) {
      isEditable = false;
    } else if (this.document.returnStatus == SupplierReturnStatus.CheckValuation) {
      isEditable = ['unitPrice'].includes(params.colDef.field);
    } else if (this.document.returnStatus == SupplierReturnStatus.Open) {
      switch (params.colDef.field) {
        case 'length':
          isEditable =
            params.data.dimension == ArticleProductDimension.Surface ||
            params.data.dimension == ArticleProductDimension.Volume ||
            params.data.dimension == ArticleProductDimension.Length;
          break;
        case 'width':
          isEditable =
            params.data.dimension == ArticleProductDimension.Surface ||
            params.data.dimension == ArticleProductDimension.Volume;
          break;
        case 'thickness':
          isEditable = params.data.dimension == ArticleProductDimension.Volume;
          break;
        case 'articleSerialId':
          isEditable = params.data.articleUsesSerial;
          break;
        case 'articleLotId':
          isEditable = params.data.articleProductRoleId == ArticleProductRole.Lot;
          break;
        case 'articleRangeValue1Id':
        case 'articleRangeValue2Id':
          isEditable = params.data.isRangeManagement;
          break;
        default:
          isEditable = ['code', 'quantity', 'unitPrice', 'discount', 'stockLocationId'].includes(
            params.colDef.field,
          );
          break;
      }
    }

    return isEditable && super.isCellEditable(params);
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];

    return this.contextMenuItems(params);
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.service.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
            true,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.service.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.service.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.service.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        case 'length':
          observable = this.service.setLineLength(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.length,
          );
          break;
        case 'width':
          observable = this.service.setLineWidth(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.width,
          );
          break;
        case 'thickness':
          observable = this.service.setLineThickness(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.thickness,
          );
          break;
        case 'articleSalesUnitCode':
          observable = this.service.setLineArticleSalesUnitId(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleSalesUnitCode,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    obs.pipe(first()).subscribe({
      next: response => {
        if (response) this._updateLines(response);
      },
      error: _ => {
        let oldData = event.data;
        oldData[event.colDef.field] = event.oldValue;
        this.lineGrid.grid.grid.api.applyTransaction({
          update: [oldData],
        });
      },
    });
  };
}
